/**
 * Service for persisting UTM parameters in localStorage.
 *
 * UTMs are marketing tracking parameters that need to survive page refreshes
 * during the registration flow. This service handles storage, retrieval,
 * and cleanup of these parameters.
 */

export interface UTMData {
	utm_id?: string
	utm_source?: string
	utm_medium?: string
	utm_campaign?: string
	utm_content?: string
	referral_id?: string
}

class UTMService {
	private readonly STORAGE_KEY = 'bt_registration_utms'

	/**
	 * Retrieves stored UTM data from localStorage
	 * @returns The stored UTM data or an empty object if none exists
	 */
	get(): UTMData {
		try {
			const stored = localStorage.getItem(this.STORAGE_KEY)
			if (stored) {
				return JSON.parse(stored) as UTMData
			}
		} catch {
			// Invalid JSON or storage error - ignore
		}
		return {}
	}

	/**
	 * Saves UTM data to localStorage
	 * Only saves if at least one UTM parameter has a value
	 * @param utms - The UTM data to store
	 */
	save(utms: UTMData): void {
		const hasAnyUTM = Object.values(utms).some((v) => v !== undefined)
		if (hasAnyUTM) {
			// Remove undefined values before storing
			const cleaned = Object.fromEntries(
				Object.entries(utms).filter(([, v]) => v !== undefined)
			)
			localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cleaned))
		}
	}

	/**
	 * Merges new UTM data with existing stored data
	 * New values take priority over stored ones
	 * @param newUtms - The new UTM data to merge
	 */
	merge(newUtms: UTMData): void {
		const hasNewUTMs = Object.values(newUtms).some((v) => v !== undefined)
		if (hasNewUTMs) {
			const merged = { ...this.get(), ...newUtms }
			this.save(merged)
		}
	}

	/**
	 * Clears all stored UTM data from localStorage
	 * Should be called after successful registration
	 */
	clear(): void {
		localStorage.removeItem(this.STORAGE_KEY)
	}
}

export const utmService = new UTMService()
